import { createTheme } from '@mui/material/styles';

// Images
import bookTeacherImage from './assets/bookteacher.jpg';
import homeFooterImage from './assets/home-footer.jpg';
import teacherTrainingImage from './assets/teacher-training.jpeg';

// colors
const colors = {
    primary: {
        light: '#2FA4DA',
        main: '#1E7CA8',
        dark: '#155979',
        contrastText: '#ffffff',
    },
    secondary: {
        light: '#C1AD95',
        main: '#626262',
        dark: '#7C6549',
        contrastText: '#ffffff',
    },
    third: {
        light: '#F0FBFF',
        main: '#E8F8FF',
        dark: '#C3EDFF',
        contrastText: '#ffffff',
    },
    forth: {
        light: '#FAEDDE',
        main: '#F5DCBF',
        dark: '#ECBD87',
        contrastText: '#ffffff',
    },
    fifth: {
        light: '#C8E9F9',
        main: '#9DD9F5',
        dark: '#50BBED',
        contrastText: '#ffffff',
    },
    error: { main: '#f44336', dark: '#C9170B' },
    warning: { main: '#ff9800' },
    info: { main: '#2196f3' },
    sexyerror: { main: '#eb5d5d' },
    sexysuccess: { main: '#6fc585' },
    success: {
        light: '#81c784',
        main: '#4caf50',
        dark: '#388e3c',
    },
    black: { main: '#626b74', dark: 'rgb(36, 40, 44)' },
    white: { main: '#ffffff' },
    background: { main: '#f0f2f5' },
};

// const background = "#f5f5f5";
// const warningLight = "rgba(253, 200, 69, .3)";
// const warningMain = "rgba(253, 200, 69, .5)";
//const warningDark = "rgba(253, 200, 69, .7)";

// border
// const borderWidth = 2;
//const borderColor = "rgba(0, 0, 0, 0.13)";

// breakpoints
const xl = 1600; //1536
const lg = 1300; //1200
const md = 1050; //900
const sm = 600;
const xs = 0;

// spacing
//const spacing = 8;

// primary: {
//     light: '#99D6DF',
//     main: '#4bb5c6',
//     dark: '#2F8896',
//     contrastText: '#ffffff',
// },
// secondary: {
//     light: '#F9BFD2',
//     main: '#f48fb1',
//     dark: '#EF5D8E',
//     contrastText: '#ffffff',
// },

// A custom theme for airTeachr
const theme = createTheme({
    palette: {
        primary: {
            light: colors.primary.light,
            main: colors.primary.main,
            dark: colors.primary.dark,
            contrastText: colors.primary.contrastText,
        },
        secondary: {
            light: colors.secondary.light,
            main: colors.secondary.main,
            dark: colors.secondary.dark,
            contrastText: colors.secondary.contrastText,
        },
        third: {
            light: colors.third.light,
            main: colors.third.main,
            dark: colors.third.dark,
            contrastText: colors.third.contrastText,
        },
        forth: {
            light: colors.forth.light,
            main: colors.forth.main,
            dark: colors.forth.dark,
            contrastText: colors.forth.contrastText,
        },
        fifth: {
            light: colors.fifth.light,
            main: colors.fifth.main,
            dark: colors.fifth.dark,
            contrastText: colors.fifth.contrastText,
        },
        error: { main: colors.error.main },
        warning: { main: colors.warning.main },
        info: { main: colors.info.main },
        success: {
            light: colors.success.light,
            main: colors.success.main,
            dark: colors.success.dark,
        },
        sexysuccess: {
            main: colors.sexysuccess.main,
        },
        sexyerror: {
            main: colors.sexyerror.main,
        },
        black: { main: colors.black.main, dark: colors.black.dark },
        white: { main: colors.white.main },
        background: { main: colors.background.main },
        tonalOffset: 0.2,
    },
    zIndex: {
        modal: 40,
    },
    breakpoints: {
        values: {
            xs,
            sm,
            md,
            lg,
            xl,
        },
    },
    typography: {
        // fontFamily: "'Roboto','Helvetica','Arial', sans-serif",
        //fontFamily: 'Quicksand, Roboto, Helvetica, Arial, sans-serif',
        fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
        h1: {
            /* previously h1.pages */
            fontSize: '3rem',
            fontStyle: 'normal',
            fontWeight: '300',
            lineHeight: '1.167',
            color: colors.primary.main,
            '@media (max-width:600px)': {
                fontSize: '2.5rem',
            },
        },
        h2: {
            fontSize: '2.75rem',
            fontStyle: 'normal',
            fontWeight: '300',
            lineHeight: '1.2',
            color: colors.primary.main,
            padding: '10px 0',
            '@media (max-width:600px)': {
                fontSize: '1.75rem',
            },
        },
        h3: {
            fontSize: '1.8rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '1.167',
            color: colors.primary.dark,
        },
        h4: {
            fontSize: '2.125rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '1.235',
            color: colors.secondary.main,
            '@media (max-width:600px)': {
                fontSize: '1.5rem',
            },
        },
        h5: {
            fontSize: '1.5rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '1.334',
            color: colors.primary.dark,
        },
        h6: {
            fontSize: '1.25rem',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: '1.6',
            color: colors.secondary.dark,
        },
        subtitle1: {
            /* previously h1 */
            fontSize: '1rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '1.75',
            color: colors.secondary.light,
        },
        subtitle2: {
            /* previously h2.subtitle */
            fontSize: '0.875rem',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: '1.57',
            color: colors.secondary.dark,
        },
        body1: {
            /* previously p */
            fontSize: '1rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '1.5',
            color: colors.black.main,
        },
        body2: {
            /* previously p */
            fontSize: '0.875rem',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '1.43',
            color: colors.black.main,
        },
        caption: {
            /* previously p.caption */
            fontSize: '14px',
            fontStyle: 'normal',
            fontWeight: '500',
            lineHeight: '20px',
            color: colors.primary.main,
        },
    },
    components: {
        // MuiCssBaseline: {
        //     styleOverrides: `
        //     @font-face {
        //         font-family: 'Quicksand';
        //         font-style: normal;
        //         font-display: swap;
        //         font-weight: 400;
        //         src: local('Quicksand'), local('Quicksand-Regular'), url(${QuicksandRegular}) format('tff');
        //         unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
        //       }
        //     `,
        // },
        SideDrawerRoot: {
            styleOverrides: {
                root: {
                    height: '100%',
                    background: 'rgb(34, 34, 34)',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '88%',
                    maxWidth: '400px',
                    zIndex: '0',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.3s ease-out',
                    paddingTop: '5%',
                },
                open: {
                    transform: 'translateX(0)',
                    boxShadow: '1px 0px 7px rgba(0, 0, 0, 0.5)',
                },
            },
            variants: [
                {
                    props: { open: true },
                    style: {},
                },
            ],
        },
        DayPaper: {
            styleOverrides: {
                paperNoPad: {
                    height: '100%',
                    padding: '4px',
                    textAlign: 'center',
                    color: colors.secondary.main,
                    whiteSpace: 'nowrap',
                },
                paperToday: {
                    height: '100%',
                    padding: '4px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    backgroundColor: colors.white.main,
                },
                paperDisabled: {
                    height: '100%',
                    padding: '4px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    backgroundColor: '#e9ecef99',
                },
                oldBooking: {
                    height: '100%',
                    padding: '4px',
                    textAlign: 'center',
                    whiteSpace: 'nowrap',
                    backgroundColor: 'rgb(40 195 80)',
                    color: colors.white.main,
                },
            },
            variants: [
                {
                    props: { previous: true, isToday: true },
                    style: {},
                },
            ],
        },
        numberTypography: {
            styleOverrides: {
                root: {
                    fontSize: '12px',
                    padding: '1px 4px',
                    margin: '0px 0px 2px',
                },
                calNumberToday: {
                    color: colors.white.main,
                    backgroundColor: colors.forth.dark,
                    borderRadius: '50%',
                    fontSize: '12px',
                    width: '21px',
                    padding: '1px 4px',
                    margin: '0px 0px 2px',
                },
            },
            variants: [
                {
                    props: { isToday: true },
                    style: {},
                },
            ],
        },
        bookingTypography: {
            styleOverrides: {
                root: {
                    'background-color': 'rgb(40 182 195)',
                    'border-radius': '4px',
                    color: colors.white.main,
                    width: '100%',
                    padding: '2px',
                },
                appointment: {
                    'background-color': 'rgb(40 182 195)',
                    'border-radius': '4px',
                    color: colors.white.main,
                    width: '100%',
                    padding: '2px',
                },
                oldAppointment: {
                    'background-color': 'rgb(19 158 210)',
                    'border-radius': '4px',
                    color: colors.white.main,
                    width: '100%',
                    padding: '2px',
                },
                appointmentOnHold: {
                    'background-color': '#f3a1f4',
                    'border-radius': '3px',
                    color: colors.white.main,
                    width: '100%',
                    padding: '2px',
                },
                oldAppointmentOnHold: {
                    'background-color': '#f372f4',
                    'border-radius': '3px',
                    color: colors.white.main,
                    width: '100%',
                    padding: '2px',
                },
                appointmentConfirmed: {
                    'background-color': 'rgb(40 195 80)',
                    'border-radius': '3px',
                    color: colors.white.main,
                    width: '100%',
                    padding: '2px',
                },
                oldAppointmentConfirmed: {
                    'background-color': 'rgb(40 139 80)',
                    'border-radius': '3px',
                    color: colors.white.main,
                    width: '100%',
                    padding: '2px',
                },
            },
            variants: [
                {
                    props: { todaysAppt: { status: 'Available' } },
                    style: {},
                },
            ],
        },
        CalendarArrow: {
            styleOverrides: {
                root: {
                    color: colors.white.main,
                    backgroundColor: colors.white.main,
                    whiteSpace: 'nowrap',
                    padding: '4px 20px',
                    '&:hover': {
                        'animation': 'grow 200ms forwards',
                    },
                },
                left: {
                    float: 'left',
                },
                right: {
                    float: 'right',
                },
            },
            variants: [
                {
                    props: { left: true },
                    style: {},
                },
            ],
        },
    },
    overrides: {
        MuiCssBaseline: {
            // styleOverrides: `
            //     @font-face {
            //         font-family: 'Quicksand';
            //         font-style: normal;
            //         font-display: swap;
            //         font-weight: 400;
            //         src: local('Quicksand'), local('Quicksand-Regular'), url(${QuicksandRegular}) format('tff');
            //         unicodeRange: U+0000-00FF, U+0131, U+0152-0153, U+02BB-02BC, U+02C6, U+02DA, U+02DC, U+2000-206F, U+2074, U+20AC, U+2122, U+2191, U+2193, U+2212, U+2215, U+FEFF;
            //       }
            //     `,
            '@global': {
                html: {
                    WebkitFontSmoothing: 'auto',
                },
                body: {
                    backgroundColor: colors.background.main,
                    margin: 0,
                    padding: 0,
                    '-webkit-font-smoothing': 'antialiased',
                    '-moz-osx-font-smoothing': 'grayscale',
                },
                '#page-container': {
                    position: 'relative',
                    minHeight: '100vh',
                },
                '#content-wrap': {
                    paddingBottom: '2.5rem' /* Footer height */,
                },
                '#footer': {
                    position: 'absolute',
                    bottom: 0,
                    width: '100%',
                    height: '2.5rem' /* Footer height */,
                },
                '.full': {
                    width: '100%',
                    paddingTop: '100px',
                },
                '.hero': {
                    backgroundColor: colors.white.main,
                },
                '.padding': {
                    padding: '44px',
                },
                '.nav-padding': {
                    padding: '14px 20px',
                },
                '.center': {
                    textAlign: 'center',
                    margin: 'auto',
                },

                img: {
                    width: '100%',
                    borderRadius: '5px',
                    border: 'none !important',
                },
                code: {
                    fontFamily:
                        "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
                },
                '.MuiButton-root': {
                    fontFamily: "'Barlow', sans-serif !important",

                    fontWeight: '700 !important',
                    fontSize: '14px !important',
                    textAlign: 'center',
                    textTransform: 'uppercase !important',
                    borderRadius: '5px !important',
                    letterSpacing: '2px',
                    transition: 'background 200ms ease 0s !important',
                    boxSizing: 'border-box',
                    zIndex: '4',
                    height: '60px',
                    width: '200px',
                    left: 0,
                    top: 0,
                    padding: 0,
                    cursor: 'pointer',
                    boxShadow: 'none',
                },
                footer: {
                    boxShadow: 'none',
                    padding: 0,
                    minHeight: 'auto',
                    '& .bg-dark': {
                        backgroundColor: '#050b25 !important',
                    },
                    '& .MuiTypography-body1': {
                        color: 'rgb(255, 255, 255)',
                    },
                    '& .home-footer': {},
                },
                '.footer-button': {
                    fontFamily: "'Barlow', sans-serif !important",

                    fontWeight: '700 !important',
                    fontSize: '14px !important',
                    textAlign: 'center',
                    borderRadius: '5px !important',
                    letterSpacing: '2px',
                    color: 'rgb(255, 255, 255) !important',
                    background: 'none rgb(3, 180, 198) !important',
                    transition: 'background 200ms ease 0s !important',
                    boxSizing: 'border-box',
                    zIndex: '4',
                    padding: 0,
                    cursor: 'pointer',
                    boxShadow: 'none',
                    height: '45px',
                    width: '150px',
                    float: 'right',
                },
                '.home-footer': {
                    minHeight: '429px !important',
                    backgroundImage: `url('${homeFooterImage}')`,
                    boxSizing: 'border-box',
                    zIndex: '1',
                    backgroundColor: 'rgba(255, 255, 255, 0)',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    borderRadius: '0px',
                },
                '.home-teacher-footer': {
                    minHeight: '429px !important',
                    backgroundImage: `url('${teacherTrainingImage}')`,
                    boxSizing: 'border-box',
                    zIndex: '1',
                    backgroundColor: 'rgba(255, 255, 255, 0)',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    borderRadius: '0px',
                },
                '.book-teacher-footer': {
                    minHeight: '429px !important',
                    backgroundImage: `url('${bookTeacherImage}')`,
                    boxSizing: 'border-box',
                    zIndex: '1',
                    backgroundColor: 'rgba(255, 255, 255, 0)',
                    backgroundRepeat: 'no-repeat',
                    backgroundSize: 'cover',
                    borderRadius: '0px',
                },
                '.home-footer .gradient, .home-teacher-footer .gradient, .book-teacher-footer .gradient':
                    {
                        zIndex: '8',
                        background:
                            '-webkit-linear-gradient(top, rgba(0, 0, 0, 0) 0%, rgb(0, 0, 0) 100%)',
                    },
                '.home-footer h6, .home-footer p, .home-teacher-footer h6, .home-teacher-footer p, .book-teacher-footer h2, .book-teacher-footer p':
                    {
                        color: 'rgb(255, 255, 255)',
                    },
                /* Content.js */
                '.next-steps .fa-link': {
                    /* used in Content.js */
                    marginRight: '5px',
                },
                /* Fix for use only flexbox in content area */
                '.next-steps .row': {
                    /* used in Content.js */
                    marginBottom: 0,
                },
                '.next-steps .col-md-5': {
                    /* used in Content.js */
                    marginBottom: '3rem',
                },
                '.spinner': {
                    /* for Loading.js loading screen*/
                    position: 'absolute',
                    display: 'flex',
                    justifyContent: 'center',
                    height: '100vh',
                    width: '100vw',
                    backgroundColor: 'white',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    right: 0,
                },
                '.result-block-container .result-block': {
                    /* found in ExternalApi.js */
                    opacity: '1',
                },
                [`@media (max-width: 1020px)`]: {
                    '.nav-padding': {
                        /* used in MakeAppt.js, SideDrawer.js, Toolbar.js */
                        padding: '14px',
                    },
                    '.login, .logout': {
                        /* used in MakeAppt.js, SideDrawer.js, Toolbar.js */
                        width: '150px',
                        margin: 'auto',
                    },
                },
                [`@media (max-width: 768px)`]: {
                    /* used in Content.js */
                    '.next-steps .col-md-5': {
                        marginBottom: 0,
                    },
                },
                '.nav-user-profile': {
                    /* used in MakeAppt.js, SideDrawer.js, and ToolBar.js */
                    width: '60px',
                    height: '60px',
                    boxShadow: '0 0 4px 0 #b3bac7',
                },
                '.toolbar': {
                    /* used in ToolBar.js */
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    background: 'rgb(250, 250, 250)',
                    height: '110px',
                    zIndex: '50',
                },
                '.toolbar__navigation': {
                    /* used in ToolBar.js */
                    display: 'flex',
                    height: '110px',
                    alignItems: 'center',
                    padding: '0 1rem',
                },
                '.toolbar__logo': {
                    /* used in ToolBar.js */
                    marginLeft: '1rem',
                    backgroundColor: 'green',
                },
                [`@media only screen and (min-width: 600px)`]: {
                    '.toolbar__logo img': {
                        /* used in ToolBar.js */
                        width: 'auto',
                        maxHeight: '36px',
                    },
                },
                [`@media only screen and (max-width: 599px)`]: {
                    '.toolbar__logo img': {
                        /* used in ToolBar.js */
                        width: 'auto',
                        maxHeight: '36px',
                        padding: '2px 0px',
                    },
                },
                '.toolbar__logo a': {
                    /* used in ToolBar.js */
                    color: 'white',
                    textDecoration: 'none',
                },
                '.spacer': {
                    /* used in ToolBar.js */
                    flex: '1',
                },
                '.toolbar_nav-items ul': {
                    /* used in ToolBar.js */
                    listStyle: 'none',
                    margin: 0,
                    padding: 0,
                    display: 'flex',
                },
                '.nav-item li': {
                    /* used in MakeAppt.css and ToolBar.js */
                    padding: '17px 0px',
                },
                '.rounded-circle': {
                    /* used in index.css, MakeAppt.js, SideDrawer.js, and ToolBar.js */
                    borderRadius: '20% !important',
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    display: 'block',
                },
                '.toolbar_nav-items a': {
                    /* used in ToolBar.js */
                    color: 'rgb(3, 180, 198)',
                    textDecoration: 'none',
                },
                [`@media (max-width: 888px)`]: {
                    '.toolbar_nav-items': {
                        /* used in ToolBar.js */
                        display: 'none',
                    },
                    '.toolbar__toggle-button': {
                        /* used in ToolBar.js */
                        marginRight: '15px',
                    },
                },
                [`@media (min-width: 889px)`]: {
                    '.toolbar__toggle-button': {
                        /* used in ToolBar.js */
                        display: 'none',
                    },
                    '.toolbar__logo': {
                        /* used in ToolBar.js */
                        marginLeft: '5px',
                    },
                },

                '.side-drawer': {
                    /* used in SideDrawer.js */
                    height: '100%',
                    background: 'rgb(34, 34, 34)',
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '88%',
                    maxWidth: '400px',
                    zIndex: '200',
                    transform: 'translateX(-100%)',
                    transition: 'transform 0.3s ease-out',
                    paddingTop: '10%',
                },
                '.side-drawer.open': {
                    /* used in SideDrawer.js */
                    transform: 'translateX(0)',
                    boxShadow: '1px 0px 7px rgba(0, 0, 0, 0.5)',
                },
                '.side-drawer ul': {
                    /* used in SideDrawer.js */
                    height: '100%',
                    listStyle: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                    margin: 'auto',
                    padding: '30px 0',
                    /*justify-content: center;*/
                },
                '.side-drawer li': {
                    /* used in SideDrawer.js */
                    margin: '0.5rem 0',
                },
                '.side-drawer a': {
                    /* used in SideDrawer.js */
                    color: 'rgb(250, 250, 250)',
                    textDecoration: 'none',
                    fontSize: '1.5rem',
                },
                '.side-drawer a:hover, .side-drawer a:active': {
                    /* used in SideDrawer.js */
                    color: 'rgb(3, 180, 198)',
                },
                [`@media (min-width: 888px)`]: {
                    /* used in SideDrawer.js */
                    '.side-drawer': {
                        display: 'none',
                    },
                },
                [`@media (max-width: 887px)`]: {
                    /* used in MakeAppt.css and ToolBar.js */
                    '.nav-item .nav-link': {
                        padding: '16px 0',
                    },
                    '.d-inline-block': {
                        /* used in MakeAppt.js and SideDrawer.js */
                        width: '180px',
                        wordWrap: 'break-word',
                    },
                    '.nav-padding': {
                        /* used in MakeAppt.js, SideDrawer.js, Toolbar.js  */
                        padding: '11px',
                    },
                    '.login, .logout': {
                        /* used in MakeAppt.js, SideDrawer.js, Toolbar.js */
                        width: '150px',
                        margin: 'auto',
                    },
                },
                '.toggle-button': {
                    /* used in DrawerToggleButton.js and ToolBar.js */
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                    height: '24px',
                    width: '30px',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: 0,
                    boxSizing: 'border-box',
                },
                '.toggle-button:focus': {
                    outline: 'none',
                },
                '.toggle-button__line': {
                    width: '30px',
                    height: '2px',
                    background: '#85ceeb',
                },
                '.backdrop': {
                    /* used in NavBar.js and Backdrop.js */
                    position: 'fixed',
                    width: '100%',
                    height: '100%',
                    top: 0,
                    left: 0,
                    background: 'rgba(0, 0, 0, 0.3)',
                    zIndex: '100',
                },
            },
        },
    },
});

export default theme;
